import React, { useState } from "react";
import { Search, User, Mail, Phone, CreditCard, X, ChevronDown, ChevronUp, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Cardholder } from "@/api/entities";
import { motion, AnimatePresence } from "framer-motion";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export default function CardholderSection({ selectedCardholder, onCardholderSelect }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("account_id");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter a search term");
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
      // In a real app, you would search the database for cardholders
      const allCardholders = await Cardholder.list();
      
      // Filter based on search type and query
      const results = allCardholders.filter(c => {
        if (searchType === "account_id") {
          return c.account_id.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchType === "email") {
          return c.email.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchType === "name") {
          return c.full_name.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return false;
      });
      
      setSearchResults(results);
      
      if (results.length === 0) {
        setError(`No cardholders found with this ${searchType.replace('_', ' ')}.`);
      }
    } catch (err) {
      console.error("Error searching cardholders:", err);
      setError("An error occurred while searching. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const selectCardholder = (cardholder) => {
    onCardholderSelect(cardholder);
    setSearchResults([]);
    setSearchQuery("");
    setExpanded(false);
  };

  const clearSelectedCardholder = () => {
    onCardholderSelect(null);
    setExpanded(true);
  };

  return (
    <Card className="mb-6 border-l-4 border-l-blue-500 shadow-md">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle 
          className="text-lg font-semibold flex items-center cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <User className="mr-2 h-5 w-5 text-blue-500" />
          Cardholder Information
          {expanded ? 
            <ChevronUp className="ml-2 h-4 w-4" /> : 
            <ChevronDown className="ml-2 h-4 w-4" />
          }
        </CardTitle>
        {selectedCardholder && (
          <div className="flex items-center">
            <span className="text-sm bg-green-100 text-green-800 py-1 px-3 rounded-full flex items-center">
              <Check className="mr-1 h-3 w-3" />
              Cardholder Selected
            </span>
          </div>
        )}
      </CardHeader>
      
      <AnimatePresence>
        {expanded && !selectedCardholder && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="pb-6">
              <div className="flex flex-col space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Search by:</div>
                  <RadioGroup
                    value={searchType}
                    onValueChange={setSearchType}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="account_id" id="account_id" />
                      <Label htmlFor="account_id">Account ID</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="email" id="email" />
                      <Label htmlFor="email">Email</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="name" id="name" />
                      <Label htmlFor="name">Name</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="flex gap-3">
                  <div className="relative flex-grow">
                    <Input
                      type="text"
                      placeholder={`Enter ${searchType.replace('_', ' ')}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="pl-10"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  <Button 
                    onClick={handleSearch} 
                    disabled={isLoading || !searchQuery.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isLoading ? "Searching..." : "Search"}
                  </Button>
                </div>
                
                {error && (
                  <div className="text-sm text-red-500 font-medium">
                    {error}
                  </div>
                )}
                
                {searchResults.length > 0 && (
                  <div className="mt-2 border rounded-md">
                    {searchResults.map((cardholder) => (
                      <div 
                        key={cardholder.id} 
                        className="p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                        onClick={() => selectCardholder(cardholder)}
                      >
                        <div>
                          <div className="font-medium">{cardholder.full_name}</div>
                          <div className="text-sm text-gray-500 flex flex-col">
                            <span className="inline-flex items-center">
                              <CreditCard className="h-3 w-3 mr-1" /> 
                              Account ID: {cardholder.account_id}
                            </span>
                            <span className="inline-flex items-center">
                              <Mail className="h-3 w-3 mr-1" /> 
                              {cardholder.email}
                            </span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          Select
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </motion.div>
        )}
        
        {selectedCardholder && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <CardContent className="py-2">
              <div className="flex justify-between items-center">
                <div className="grid grid-cols-2 gap-x-12 gap-y-2 flex-grow">
                  <div>
                    <div className="text-sm text-gray-500">Account ID</div>
                    <div className="font-medium">{selectedCardholder.account_id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Full Name</div>
                    <div className="font-medium">{selectedCardholder.full_name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <div className="font-medium">{selectedCardholder.email}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <div className="font-medium">{selectedCardholder.phone}</div>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={clearSelectedCardholder}
                  className="text-gray-400 hover:text-gray-600 ml-2"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}